/**
 * 計助ダッシュボード: データ表のレイアウト方針
 *
 * - 正規データは常にストアの `gridData` + `gridHeaders`（長形式互換）。
 * - 見た目は共通スタイル（KeisukeUnifiedDataGrid.module.scss）＋セル入力の同一パターンを基本とし、
 *   解析・入力形式ごとに「行列の意味・ラベル位置・仮想化の要否」だけを切り替える。
 * - DOM を1枚の同型 table に無理に統合しない（2要因ブロック幾何と長形式万行は別レンダラ）。
 */

import type { InputFormTypeKey } from '../../../../stores/systems/keisuke/dashboardStore';

export type KeisukeDashboardDataGridLayoutKind =
  /** 1行1観測のスタック表。仮想スクロール＋表示用行パディング */
  | 'longStackedVirtual'
  /** 用量反応: 用量・反応数・総数の3列コンパクト表 */
  | 'doseResponseCompact'
  /** 生存曲線: 群・時間・イベントの3列コンパクト表 */
  | 'survivalCompact'
  /** 反復測定: 被験体・因子・値の3列コンパクト表 */
  | 'repeatedCompact'
  /** t検定2列: ヘッダー行＋2列の列行列 */
  | 'columnMatrixTwoCol'
  /** 1要因ANOVA: ヘッダー行＋可変列数の列行列 */
  | 'columnMatrixMulti'
  /** 2要因ANOVA: 因子ブロック表＋セルあたり n */
  | 'twoWayFactorBlock';

export function getKeisukeDashboardDataGridLayout(
  inputFormType: InputFormTypeKey
): KeisukeDashboardDataGridLayoutKind {
  switch (inputFormType) {
    case 'tTestTwoColumns':
      return 'columnMatrixTwoCol';
    case 'oneWayColumns':
      return 'columnMatrixMulti';
    case 'twoWayBlocks':
      return 'twoWayFactorBlock';
    case 'doseResponseForm':
      return 'doseResponseCompact';
    case 'survivalForm':
      return 'survivalCompact';
    case 'repeatedForm':
      return 'repeatedCompact';
    case 'longFormat':
      return 'longStackedVirtual';
    default:
      return 'longStackedVirtual';
  }
}

/** 長形式仮想表以外は内容高さ（縦に砂漠を伸ばさない） */
export function keisukeDashboardGridAreaUsesShrinkWrap(mode: InputFormTypeKey): boolean {
  return getKeisukeDashboardDataGridLayout(mode) !== 'longStackedVirtual';
}

/**
 * 左レールが薄い入力形式（設定が中央へ同居済み）。
 * カラム幅の上限適用に使う。
 */
export function keisukeDashboardLeftRailIsSparse(mode: InputFormTypeKey): boolean {
  return mode === 'tTestTwoColumns';
}

/**
 * 計助ダッシュボードのデータ列・2要因の行見出し列など、表の列の初期幅（px）。
 * 長形式・t検定2列・1要因列・2要因因子Aで共有し、中央ペインの state と同期する。
 */
export const KEISUKE_DASHBOARD_DATA_COL_WIDTH_DEFAULT_PX = 120;
export const KEISUKE_DASHBOARD_DATA_COL_WIDTH_MIN_PX = 112;
export const KEISUKE_DASHBOARD_DATA_COL_WIDTH_MAX_PX = 640;

export function clampKeisukeDashboardDataColWidthPx(raw: number): number {
  return Math.min(
    KEISUKE_DASHBOARD_DATA_COL_WIDTH_MAX_PX,
    Math.max(KEISUKE_DASHBOARD_DATA_COL_WIDTH_MIN_PX, Math.round(raw))
  );
}

/** 行番号列（#）の既定幅。長形式仮想表と揃える */
export const KEISUKE_DASHBOARD_ROW_NUM_COL_WIDTH_PX = 32;

/**
 * ビューポート幅いっぱいにデータ列を均等配分したときの1列幅。
 * 自動フィットはドラッグ上限（MAX）で切らず、横ガターを残さない。
 */
export function keisukeDashboardFillDataColWidthPx(
  viewportInnerPx: number,
  columnCount: number,
  rowNumWidthPx: number = KEISUKE_DASHBOARD_ROW_NUM_COL_WIDTH_PX
): number {
  const n = Math.max(1, Math.floor(columnCount));
  if (!(viewportInnerPx > 0)) return KEISUKE_DASHBOARD_DATA_COL_WIDTH_DEFAULT_PX;
  const raw = Math.round((viewportInnerPx - rowNumWidthPx) / n);
  return Math.max(KEISUKE_DASHBOARD_DATA_COL_WIDTH_MIN_PX, raw);
}
